/**
 * Browser localStorage provider.
 *
 * Simplest possible persistence — good for tiny deployments or as a fallback
 * when IndexedDB is unavailable (private windows, locked-down kiosks). Stores
 * each collection as a JSON blob under a namespaced key. Not suitable for
 * millions of rows (synchronous, ~5MB cap), but correct and dependency-free.
 */

import type {
  Combination,
  ExtensionConfig,
  Favorite,
  UsageEvent,
} from "@/types";
import { STORAGE_KEYS, type StorageProvider } from "./types";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export class LocalStorageProvider implements StorageProvider {
  readonly kind = "local";

  async init(): Promise<void> {
    // Probe availability; throws (caught by factory) in disabled environments.
    const probe = "spc:__probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
  }

  async getCombinations(): Promise<Combination[]> {
    return read<Combination[]>(STORAGE_KEYS.combinations, []);
  }

  async putCombination(combination: Combination): Promise<void> {
    const all = await this.getCombinations();
    const idx = all.findIndex((c) => c.id === combination.id);
    if (idx >= 0) all[idx] = combination;
    else all.push(combination);
    write(STORAGE_KEYS.combinations, all);
  }

  async deleteCombination(id: string): Promise<void> {
    write(
      STORAGE_KEYS.combinations,
      (await this.getCombinations()).filter((c) => c.id !== id),
    );
  }

  async getUsageEvents(): Promise<UsageEvent[]> {
    return read<UsageEvent[]>(STORAGE_KEYS.events, []);
  }

  async appendUsageEvent(event: UsageEvent): Promise<void> {
    const all = await this.getUsageEvents();
    all.push(event);
    write(STORAGE_KEYS.events, all);
  }

  async getFavorites(): Promise<Favorite[]> {
    return read<Favorite[]>(STORAGE_KEYS.favorites, []);
  }

  async putFavorite(favorite: Favorite): Promise<void> {
    const all = await this.getFavorites();
    const idx = all.findIndex((f) => f.id === favorite.id);
    if (idx >= 0) all[idx] = favorite;
    else all.push(favorite);
    write(STORAGE_KEYS.favorites, all);
  }

  async deleteFavorite(id: string): Promise<void> {
    write(
      STORAGE_KEYS.favorites,
      (await this.getFavorites()).filter((f) => f.id !== id),
    );
  }

  async getConfig(): Promise<Partial<ExtensionConfig> | null> {
    return read<Partial<ExtensionConfig> | null>(STORAGE_KEYS.config, null);
  }

  async putConfig(config: ExtensionConfig): Promise<void> {
    write(STORAGE_KEYS.config, config);
  }

  async clearAll(): Promise<void> {
    write(STORAGE_KEYS.combinations, []);
    write(STORAGE_KEYS.events, []);
    write(STORAGE_KEYS.favorites, []);
  }
}
